"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const nmr_processing_1 = require("nmr-processing");
const react_1 = require("react");
const ChartContext_1 = require("../context/ChartContext");
const DispatchContext_1 = require("../context/DispatchContext");
const ActionButtons_1 = __importDefault(require("../elements/ActionButtons"));
const Input_1 = __importDefault(require("../elements/Input"));
const InputRange_1 = __importDefault(require("../elements/InputRange"));
const Label_1 = __importDefault(require("../elements/Label"));
const Select_1 = __importDefault(require("../elements/Select"));
const useFilter_1 = require("../hooks/useFilter");
const useSpectrum_1 = __importDefault(require("../hooks/useSpectrum"));
const Header_1 = require("./Header");
const HeaderContainer_1 = require("./HeaderContainer");
const selectStyle = {
    marginLeft: '5px',
    marginRight: '10px',
    border: 'none',
    height: '20px',
};
const inputStyle = {
    input: {
        width: '70px',
        textAlign: 'center',
    },
    inputWrapper: {
        height: '100%',
    },
};
const phaseCorrectionTypes = {
    manual: 'manual',
    automatic: 'automatic',
    absolute: 'absolute',
};
const algorithms = [
    {
        label: 'Manual',
        value: phaseCorrectionTypes.manual,
    },
    {
        label: 'Automatic',
        value: phaseCorrectionTypes.automatic,
    },
    {
        label: 'Absolute',
        value: phaseCorrectionTypes.absolute,
    },
];
const emptyData = { datum: {}, filter: null };
function PhaseCorrectionPanel() {
    const { toolOptions: { data: { pivot }, }, } = (0, ChartContext_1.useChartData)();
    const { data } = (0, useSpectrum_1.default)(emptyData);
    const filter = (0, useFilter_1.useFilter)(nmr_processing_1.Filters.phaseCorrection.id);
    const dispatch = (0, DispatchContext_1.useDispatch)();
    const [value, setValue] = (0, react_1.useState)({ ph0: 0, ph1: 0 });
    const valueRef = (0, react_1.useRef)({ ph0: 0, ph1: 0 });
    const ph0Ref = (0, react_1.useRef)();
    const ph1Ref = (0, react_1.useRef)();
    const [phaseCorrectionType, setPhaseCorrectionType] = (0, react_1.useState)(phaseCorrectionTypes.manual);
    (0, react_1.useEffect)(() => {
        if (filter && phaseCorrectionType === phaseCorrectionTypes.manual) {
            valueRef.current = filter.value;
            setValue(filter.value);
        }
        if (ph0Ref.current && ph1Ref.current) {
            if (filter) {
                ph0Ref.current.setValue(filter.value.ph0);
                ph1Ref.current.setValue(filter.value.ph1);
            }
            else {
                ph0Ref.current.setValue(valueRef.current.ph0);
                ph1Ref.current.setValue(valueRef.current.ph1);
            }
        }
    }, [filter, phaseCorrectionType]);
    const handleApplyFilter = (0, react_1.useCallback)(() => {
        switch (phaseCorrectionType) {
            case phaseCorrectionTypes.automatic: {
                dispatch({
                    type: 'APPLY_AUTO_PHASE_CORRECTION_FILTER',
                });
                break;
            }
            case phaseCorrectionTypes.manual: {
                dispatch({
                    type: 'APPLY_MANUAL_PHASE_CORRECTION_FILTER',
                    payload: value,
                });
                break;
            }
            case phaseCorrectionTypes.absolute: {
                dispatch({
                    type: 'APPLY_ABSOLUTE_FILTER',
                });
                break;
            }
            default:
                break;
        }
    }, [dispatch, phaseCorrectionType, value]);
    const calcPhaseCorrectionHandler = (0, react_1.useCallback)((newValues, filedName) => {
        if (filedName === 'ph1' && data.re) {
            const diff0 = newValues.ph0 - valueRef.current.ph0;
            const diff1 = newValues.ph1 - valueRef.current.ph1;
            newValues.ph0 +=
                diff0 - (diff1 * (data.re.length - pivot?.index)) / data.re.length;
        }
        dispatch({
            type: 'CALCULATE_MANUAL_PHASE_CORRECTION_FILTER',
            payload: newValues,
        });
    }, [data.re, dispatch, pivot?.index]);
    const updateInputRangeInitialValue = (0, react_1.useCallback)((value) => {
        // update InputRange initial value
        ph0Ref.current.setValue(value.ph0);
        ph1Ref.current.setValue(value.ph1);
    }, []);
    const handleInput = (0, react_1.useCallback)((e) => {
        const { name, value } = e.target;
        if (e.target) {
            const newValue = { ...valueRef.current, [name]: Number(value) };
            if (String(value).trim() !== '-') {
                calcPhaseCorrectionHandler(newValue, name);
            }
            updateInputRangeInitialValue(newValue);
            valueRef.current = newValue;
            setValue(valueRef.current);
        }
    }, [calcPhaseCorrectionHandler, updateInputRangeInitialValue]);
    const handleRangeChange = (0, react_1.useCallback)((e) => {
        const newValue = { ...valueRef.current, [e.name]: e.value };
        calcPhaseCorrectionHandler(newValue, e.name);
        updateInputRangeInitialValue(newValue);
        valueRef.current = newValue;
        setValue(valueRef.current);
    }, [calcPhaseCorrectionHandler, updateInputRangeInitialValue]);
    const handleCancelFilter = (0, react_1.useCallback)(() => {
        dispatch({
            type: 'RESET_SELECTED_TOOL',
        });
    }, [dispatch]);
    const onChangeHandler = (0, react_1.useCallback)((val) => {
        setPhaseCorrectionType(val);
    }, []);
    return ((0, jsx_runtime_1.jsxs)(HeaderContainer_1.HeaderContainer, { children: [(0, jsx_runtime_1.jsx)(Select_1.default, { onChange: onChangeHandler, items: algorithms, defaultValue: phaseCorrectionTypes.manual, style: selectStyle }), phaseCorrectionType === phaseCorrectionTypes.manual && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(Label_1.default, { title: "PH0 :", style: Header_1.headerLabelStyle, children: (0, jsx_runtime_1.jsx)(Input_1.default, { name: "ph0", style: inputStyle, onChange: handleInput, value: value.ph0, type: "number", debounceTime: 250 }) }), (0, jsx_runtime_1.jsx)(Label_1.default, { title: "PH1 :", style: Header_1.headerLabelStyle, children: (0, jsx_runtime_1.jsx)(Input_1.default, { name: "ph1", style: inputStyle, onChange: handleInput, value: value.ph1, type: "number", debounceTime: 250 }) }), (0, jsx_runtime_1.jsx)(InputRange_1.default, { ref: ph0Ref, name: "ph0", label: "Change PH0 (click and drag)", shortLabel: "Ph0", style: { width: '20%' }, onChange: handleRangeChange }), (0, jsx_runtime_1.jsx)(InputRange_1.default, { ref: ph1Ref, name: "ph1", label: "Change PH1 (click and drag)", shortLabel: "Ph1", style: { width: '20%' }, onChange: handleRangeChange })] })), (0, jsx_runtime_1.jsx)(ActionButtons_1.default, { onDone: handleApplyFilter, onCancel: handleCancelFilter })] }));
}
exports.default = PhaseCorrectionPanel;
